import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, CheckCircle, XCircle, Eye, User } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useLanguage } from '@/contexts/useLanguage';

interface FlaggedQuestion {
  id: string;
  question_id: string;
  flagged_by: string;
  reason: string;
  status: string;
  flagged_at: string;
  reviewed_by?: string;
  reviewed_at?: string;
  admin_notes?: string;
  custom_id?: string;
  question: {
    question_text: string;
    custom_id?: string;
    options: string[];
    correct_answer: string[];
    explanation?: string;
  };
  flagged_by_profile: Profile;
}

// Helper to parse JSON fields that may be strings or already parsed
const parseJsonField = <T,>(value: unknown, fallback: T): T => {
  if (!value) return fallback;
  if (typeof value === 'string') {
    try { return JSON.parse(value); } catch { return fallback; }
  }
  return value as T;
};

interface Profile {
  first_name?: string;
  last_name?: string;
  email?: string;
}

export function FlaggedQuestionsTab() {
  const [flaggedQuestions, setFlaggedQuestions] = useState<FlaggedQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [reviewingId, setReviewingId] = useState<string | null>(null);
  const [adminNotes, setAdminNotes] = useState('');
  const { toast } = useToast();
  const { t } = useLanguage();

  const fetchFlaggedQuestions = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('flagged_questions')
        .select('*, question:questions(*)')
        .order('flagged_at', { ascending: false });

      if (error) throw error;

      if (!data) {
        setFlaggedQuestions([]);
        return;
      }

      const userIds = [...new Set(data.map(item => item.flagged_by).filter(Boolean))];
      let profilesMap = new Map();

      if (userIds.length > 0) {
        const { data: profilesData, error: profilesError } = await supabase
          .from('profiles')
          .select('user_id, first_name, last_name, email')
          .in('user_id', userIds);

        if (profilesError) throw profilesError;
        profilesMap = new Map(profilesData.map(p => [p.user_id, p]));
      }

      const transformedData: FlaggedQuestion[] = data.map(item => ({
        id: item.id,
        question_id: item.question_id,
        flagged_by: item.flagged_by,
        reason: item.reason,
        status: item.status,
        flagged_at: item.flagged_at,
        reviewed_by: item.reviewed_by,
        reviewed_at: item.reviewed_at,
        admin_notes: item.admin_notes,
        custom_id: item.question?.custom_id || undefined,
        question: {
          question_text: item.question?.question_text || 'N/A',
          custom_id: item.question?.custom_id || undefined,
          options: parseJsonField<string[]>(item.question?.options, []),
          correct_answer: parseJsonField<string[]>(item.question?.correct_answer, []),
          explanation: item.question?.explanation
        },
        flagged_by_profile: profilesMap.get(item.flagged_by) || {}
      }));

      setFlaggedQuestions(transformedData);
    } catch (error) {
      console.error('Error fetching flagged questions:', error);
      toast({
        title: t('error.title'),
        description: t('error.description'),
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [toast, t]);

  useEffect(() => {
    fetchFlaggedQuestions();
  }, [fetchFlaggedQuestions]);

  const updateFlagStatus = async (flagId: string, status: 'approved' | 'rejected') => {
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) return;

      const { error } = await supabase
        .from('flagged_questions')
        .update({
          status: status,
          reviewed_by: user.user.id,
          reviewed_at: new Date().toISOString(),
          admin_notes: adminNotes || null
        })
        .eq('id', flagId);

      if (error) throw error;

      setFlaggedQuestions(prev => 
        prev.map(fq => 
          fq.id === flagId 
            ? { ...fq, status, reviewed_by: user.user.id, reviewed_at: new Date().toISOString(), admin_notes: adminNotes }
            : fq
        )
      );

      setReviewingId(null);
      setAdminNotes('');

      toast({
        title: t('statusUpdated.title'),
        description: t('statusUpdated.description', { status }),
      });
    } catch (error) {
      console.error('Error updating flag status:', error);
      toast({
        title: t('error.title'),
        description: t('error.description'),
        variant: "destructive"
      });
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="secondary"><AlertTriangle className="w-3 h-3 mr-1" />{t('pending')}</Badge>;
      case 'approved':
        return <Badge variant="destructive"><CheckCircle className="w-3 h-3 mr-1" />{t('approved')}</Badge>;
      case 'rejected':
        return <Badge variant="default"><XCircle className="w-3 h-3 mr-1" />{t('rejected')}</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatUserName = (profile: Profile) => {
    if (profile.first_name && profile.last_name) {
      return `${profile.first_name} ${profile.last_name}`;
    }
    return profile.email || t('unknownUser');
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader>
              <div className="h-4 bg-muted rounded w-3/4"></div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="h-3 bg-muted rounded w-full"></div>
                <div className="h-3 bg-muted rounded w-2/3"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">{t('flaggedQuestions.title')} ({flaggedQuestions.length})</h2>
        <div className="flex gap-2">
          <Badge variant="secondary">
            {flaggedQuestions.filter(fq => fq.status === 'pending').length} {t('pending')}
          </Badge>
          <Badge variant="destructive">
            {flaggedQuestions.filter(fq => fq.status === 'approved').length} {t('approved')}
          </Badge>
          <Badge variant="default">
            {flaggedQuestions.filter(fq => fq.status === 'rejected').length} {t('rejected')}
          </Badge>
        </div>
      </div>

      {flaggedQuestions.length === 0 ? (
        <Card>
          <CardContent className="text-center py-8">
            <AlertTriangle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">{t('noFlaggedQuestions')}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {flaggedQuestions.map((flaggedQuestion) => (
            <Card key={flaggedQuestion.id} className="border-l-4 border-l-orange-500">
              <CardHeader>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      {flaggedQuestion.custom_id && (
                        <Badge variant="outline" className="font-mono text-xs">
                          {flaggedQuestion.custom_id}
                        </Badge>
                      )}
                    </div>
                    <CardTitle className="text-lg leading-relaxed">
                      {flaggedQuestion.question.question_text}
                    </CardTitle>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        <span>{t('flaggedBy')} {formatUserName(flaggedQuestion.flagged_by_profile)}</span>
                      </div>
                      <span>•</span>
                      <span>{t('reason')}: {flaggedQuestion.reason}</span>
                      <span>•</span>
                      <span>{new Date(flaggedQuestion.flagged_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(flaggedQuestion.status)}
                    {flaggedQuestion.status === 'pending' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setReviewingId(reviewingId === flaggedQuestion.id ? null : flaggedQuestion.id)}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        {t('review')}
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Question Options */}
                <div className="space-y-2">
                  <h4 className="font-medium">{t('answerOptions')}</h4>
                  {Array.isArray(flaggedQuestion.question.options) && flaggedQuestion.question.options.map((option, index) => {
                    const isCorrect = Array.isArray(flaggedQuestion.question.correct_answer)
                      ? flaggedQuestion.question.correct_answer.includes(option)
                      : false;
                    const letter = String.fromCharCode(65 + index);
                    return (
                      <div
                        key={index}
                        className={`text-sm p-3 rounded-lg border ${isCorrect ? 'bg-green-50 border-green-200 text-green-800 dark:bg-green-950 dark:border-green-800 dark:text-green-200' : 'bg-muted/30 border-border'}`}
                      >
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground font-bold text-xs mr-3">
                          {letter}
                        </span>
                        {option}
                        {isCorrect && <CheckCircle className="inline w-4 h-4 ml-2 text-green-600" />}
                      </div>
                    );
                  })}
                </div>

                {/* Explanation */}
                {flaggedQuestion.question.explanation && (
                  <div className="p-4 bg-primary/5 border border-primary/20 rounded-xl">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                        <Eye className="h-5 w-5 text-primary" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-primary mb-2">{t('explanation')}</h4>
                        <p className="text-foreground leading-relaxed">
                          {flaggedQuestion.question.explanation}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Admin Notes */}
                {flaggedQuestion.admin_notes && (
                  <div className="p-3 bg-muted/50 rounded border">
                    <h4 className="font-medium mb-1">{t('adminNotes')}</h4>
                    <p className="text-sm">{flaggedQuestion.admin_notes}</p>
                  </div>
                )}

                {/* Review Section */}
                {reviewingId === flaggedQuestion.id && flaggedQuestion.status === 'pending' && (
                  <div className="border-t pt-4 space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="admin-notes">{t('adminNotesOptional')}</Label>
                      <Textarea
                        id="admin-notes"
                        placeholder={t('addNotes')}
                        value={adminNotes}
                        onChange={(e) => setAdminNotes(e.target.value)}
                        className="min-h-[80px]"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="destructive"
                        onClick={() => updateFlagStatus(flaggedQuestion.id, 'approved')}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        {t('approveFlag')}
                      </Button>
                      <Button
                        variant="default"
                        onClick={() => updateFlagStatus(flaggedQuestion.id, 'rejected')}
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        {t('rejectFlag')}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setReviewingId(null);
                          setAdminNotes('');
                        }}
                      >
                        {t('cancel')}
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}